import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '../utils/_partner_api_client';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CompanyWithStats {
  organizationId: string;
  companyId: string;
  companyName: string;
  totalUsers: number;
  totalMessages: number;
  lastActive: string | null;  // ISO datetime string or null
  createdOn: string | null;   // ISO datetime string or null
}

export interface CompanyListPage {
  companies: CompanyWithStats[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  from: string;
  to: string;
}

export interface UsePartnerCompaniesWithStatsParams {
  from?: string;    // YYYY-MM-DD — optional, backend defaults to last 30 days
  to?: string;      // YYYY-MM-DD — optional, backend defaults to today
  page?: number;    // optional, defaults to 1
  limit?: number;   // optional, defaults to 10
  /**
   * AceUpAdmin/AceUpViewer only — scopes the request to this organization.
   * The backend falls back to the caller's own org_id (JWT) when omitted,
   * which is *not* what an AceUp dashboard session wants once an org has
   * been picked, so callers should pass the selected org here. Ignored (and
   * unnecessary) for PartnerAdmin/PartnerViewer, whose org_id always comes
   * from the JWT.
   */
  organizationId?: string | null;
  enabled?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export const usePartnerCompaniesWithStatsQuery = ({
  from,
  to,
  page,
  limit,
  organizationId,
  enabled = true,
}: UsePartnerCompaniesWithStatsParams): UseQueryResult<CompanyListPage, Error> => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();

  return useQuery({
    queryKey: ['partner', 'companies', 'with-stats', from, to, page, limit, organizationId] as const,
    enabled: Boolean(isAuthenticated && allyApi && enabled),
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<CompanyListPage> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');

      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (page != null) qs.set('page', String(page));
      if (limit != null) qs.set('limit', String(limit));
      if (organizationId) qs.set('organization_id', organizationId);

      return fetchJson<CompanyListPage>(
        joinUrl(allyApi, `api/v1/partner/companies?${qs}`),
        authHeader,
        'Failed to fetch partner companies with stats'
      );
    },
  });
};
